import cx from 'clsx';
import * as _ from 'lodash-es';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';

import { PurePortal } from './PurePortal';
import { memo } from '../../utils';
import { getNodeFromSelector, setValueForStyles } from '../../utils/dom';
import { addEventListener } from '../../utils/event';
import { SCROLL_WIDTH } from '../../utils/scrollbarWidth';

function hasScrollbarY(element) {
	if (element === document.body) {
		return element.scrollHeight > window.innerHeight;
	}
	return element.scrollHeight > element.clientHeight;
}

function diffStyle(prev, next) {
	const result = {};
	const prevKeys = Object.keys(prev);
	for (let i = 0; i < prevKeys.length; i += 1) {
		const key = prevKeys[i];
		if (!next[key]) {
			result[key] = '';
		}
	}
	const nextKeys = Object.keys(next);
	for (let i = 0; i < prevKeys.length; i += 1) {
		const key = nextKeys[i];
		result[key] = next[key];
	}
	return result;
}

const patched = new Map();
export function patchElement(parent, scrollWidth = SCROLL_WIDTH) {
	const meta = patched.get(parent);
	if (meta) {
		meta.count += 1;
	} else {
		const { overflowY, paddingRight } = parent.style;
		const originalPadding = getComputedStyle(parent).paddingRight;
		const newPadding = parseFloat(originalPadding || '0') + scrollWidth;
		parent.style.overflowY = 'hidden';
		parent.style.paddingRight = `${newPadding}px`;
		const newMeta = {
			count: 1,
			overflowY,
			paddingRight,
		};
		patched.set(parent, newMeta);
	}
}

export function restoreElement(parent) {
	const meta = patched.get(parent);
	if (!meta) {
		throw new Error('?? cannot find meta info');
	}
	if (meta.count === 1) {
		patched.delete(parent);
		parent.style.overflowY = meta.overflowY;
		parent.style.paddingRight = meta.paddingRight;
	} else {
		meta.count -= 1;
	}
}

const MountElement = ({ node, getParent, selector }) => {
	useLayoutEffect(() => {
		const parent = getParent(selector);
		parent.appendChild(node);

		return () => {
			parent.removeChild(node);
		};
	}, [node, selector, getParent]);

	return null;
};

export const Portal = forwardRef((props, ref) => {
	const {
		visible = true,
		box = 'div',
		selector = 'body',
		className = '',
		style,

		closeClickAway = false, // 点击弹窗外空白处关闭
		closeEsc = false, // 按下 esc 按钮关闭
		freezeScroll = true, // 禁止弹窗外滚动

		onLayerReady, // 遮罩准备好时
		onClose = _.noop, // 弹窗关闭时

		children,
	} = props;

	const node = useMemo(() => {
		return document.createElement(box);
	}, [box]);
	const getParent = useMemo(() => memo(getNodeFromSelector), []);

	const propsRef = useRef(props);
	propsRef.current = props;
	const prevStyleRef = useRef(style);
	const purePortalRef = useRef(null);

	useImperativeHandle(
		ref,
		() => {
			return {
				purePortalRef,
				container: node,
				contains(node) {
					const purePortal = purePortalRef.current;
					if (!purePortal) {
						return false;
					}
					return purePortal.contains(node);
				},
			};
		},
		[node],
	);

	// update portal className.
	useLayoutEffect(() => {
		node.className = cx(`mg-portal`, className);
	}, [node, className]);
	useLayoutEffect(() => {
		const result = diffStyle(prevStyleRef.current || {}, style || {});

		setValueForStyles(node, result);
		prevStyleRef.current = style;
	}, [node, style]);

	useLayoutEffect(() => {
		if (!visible || !closeClickAway) {
			return _.noop;
		}
		const { position, top, bottom, left, right } = node.style;
		const parent = getParent(selector);

		node.style.position = parent === document.body ? 'fixed' : 'absolute';
		node.style.top = '0';
		node.style.bottom = '0';
		node.style.left = '0';
		node.style.right = '0';

		return () => {
			node.style.position = position;
			node.style.top = top;
			node.style.bottom = bottom;
			node.style.left = left;
			node.style.right = right;
		};
	}, [node, closeClickAway, visible, selector, getParent]);

	// 禁止弹窗后滚动
	useLayoutEffect(() => {
		const parent = getParent(selector);
		if (!visible || !freezeScroll || !(parent instanceof HTMLElement) || !hasScrollbarY(parent)) {
			// 不满足滚动条锁定的条件
			return _.noop;
		}
		patchElement(parent);
		return () => restoreElement(parent);
	}, [selector, visible, freezeScroll, getParent]);

	// 事件绑定 - 点击弹窗外，关闭弹窗
	useLayoutEffect(() => {
		if (!visible) {
			return _.noop;
		}

		let dispose = _.noop;
		if (closeClickAway) {
			const handleClickAway = (e) => {
				const { onClose, visible } = propsRef.current;
				const purePortal = purePortalRef.current;
				if (e.defaultPrevented || !closeClickAway || !visible || !purePortal) {
					return;
				}

				const { target } = e;
				if (!(target instanceof Node) || target === node || !purePortal.contains(target)) {
					onClose && onClose(e);
				}
			};
			// 绑定点击外层关闭相关的事件
			const disposeClickEvent = addEventListener(node, 'pointerdown', handleClickAway);

			dispose = () => {
				disposeClickEvent();
			};
		}
		const { onLayerReady } = propsRef.current;
		onLayerReady && onLayerReady(node);

		return dispose;
	}, [closeClickAway, visible, node]);

	// 事件绑定 - 按 ESC 关闭弹窗
	useEffect(() => {
		if (!visible || !closeEsc) {
			return _.noop;
		}

		function onKeyUp(e) {
			const { onClose } = propsRef.current;
			if (!onClose) {
				return;
			}

			if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
				onClose(e);
			}
		}
		return addEventListener(document.body, 'keyup', onKeyUp);
	}, [closeEsc, visible]);

	return visible && node ? (
		<PurePortal ref={purePortalRef} node={node} selector={selector}>
			{children}
		</PurePortal>
	) : null;
});
Portal.displayName = 'Portal';
